/**
 * Costanti condivise tra API (server) e UI (client).
 * Unica fonte di verità per enum, regole fiscali e default di calcolo.
 */

/** Regime fiscale del professionista. */
export const VAT_REGIMES = Object.freeze({
  FORFETTARIO: 'forfettario',
  ORDINARIO: 'ordinario',
});

/** Unità di misura ammesse a listino. */
export const SERVICE_UNITS = Object.freeze([
  { value: 'hour', label: 'Ora' },
  { value: 'day', label: 'Giorno' },
  { value: 'piece', label: 'Pezzo / voce' },
  { value: 'project', label: 'A progetto' },
  { value: 'month', label: 'Mese (canone)' },
]);

/** Categorie predefinite del listino servizi. */
export const SERVICE_CATEGORIES = Object.freeze([
  'Consulenza',
  'Sviluppo software',
  'Web & Design',
  'Contenuti & Copy',
  'Manutenzione & Supporto',
  'Formazione',
  'Altro',
]);

/** Tipologia anagrafica cliente. */
export const CLIENT_TYPES = Object.freeze([
  { value: 'individual', label: 'Persona fisica' },
  { value: 'company', label: 'Azienda / Ente' },
]);

/** Stato CRM di un cliente. */
export const CLIENT_STATUSES = Object.freeze([
  { value: 'lead', label: 'Lead' },
  { value: 'active', label: 'Attivo' },
  { value: 'inactive', label: 'Inattivo' },
  { value: 'archived', label: 'Archiviato' },
]);

/** Ciclo di vita di un preventivo. */
export const QUOTE_STATUSES = Object.freeze([
  { value: 'draft', label: 'Bozza', tone: 'slate' },
  { value: 'sent', label: 'Inviato', tone: 'sky' },
  { value: 'accepted', label: 'Accettato', tone: 'emerald' },
  { value: 'rejected', label: 'Rifiutato', tone: 'rose' },
  { value: 'expired', label: 'Scaduto', tone: 'amber' },
  { value: 'invoiced', label: 'Fatturato', tone: 'violet' },
  { value: 'cancelled', label: 'Annullato', tone: 'zinc' },
]);

/** Modalità di sconto applicato al preventivo. */
export const DISCOUNT_MODES = Object.freeze([
  { value: 'none', label: 'Nessuno sconto' },
  { value: 'percent', label: 'Percentuale' },
  { value: 'amount', label: 'Importo fisso' },
]);

/** Regola per l'imposta di bollo. */
export const STAMP_DUTY_MODES = Object.freeze([
  { value: 'auto', label: 'Automatica (se superata la soglia)' },
  { value: 'always', label: 'Sempre applicata' },
  { value: 'never', label: 'Mai applicata' },
]);

// ---------------------------------------------------------------------------
// Regime forfettario (L. 190/2014, art. 1 commi 54-89) - valori di default
// ---------------------------------------------------------------------------

/** Imposta di bollo: 2,00 € per documenti con importo > 77,47 €. */
export const STAMP_DUTY = Object.freeze({
  amount: 2,
  threshold: 77.47,
  mode: 'auto',
  regulation: "Imposta di bollo ai sensi del D.P.R. 642/1972, tab. B, punto 14.1.",
});

/**
 * Rivalsa contributiva. Il valore predefinito (4%) è la rivalsa tipica della
 * Gestione Separata INPS / casse professionali a carico del committente.
 * E' sempre modificabile per singolo preventivo.
 */
export const INPS_DEFAULTS = Object.freeze({
  enabled: true,
  rate: 4,
  fund: 'Gestione Separata INPS',
  regulation: 'Rivalsa oneri contributivi ai sensi dell\'art. 15 della L. 183/2011.',
});

/** Dicitura obbligatoria in fattura/preventivo per il regime forfettario. */
export const FORFETTARIO_LEGAL_NOTE = Object.freeze(
  'Operazione effettuata ai sensi dell\'art. 1, commi da 54 a 89, della Legge 23 dicembre 2014, n. 190 ' +
    '(regime forfettario): niente IVA in fattura, nessuna ritenuta d\'acconto all\'atto del pagamento.',
);

export const NO_WITHHOLDING_NOTE =
  'Regime forfettario: non è prevista la ritenuta d\'acconto (art. 1, c. 59-60, L. 190/2014).';

/** Template numerazione documenti: {year} e {seq:4} i placeholder supportati. */
export const NUMBERING_TEMPLATE = 'PREV-{year}-{seq:4}';

export const CURRENCY = 'EUR';
export const LOCALE = 'it-IT';
export const TIMEZONE = 'Europe/Rome';

/** Valori restituiti da GET /api/meta per popolare i select della UI. */
export const ENUMS = Object.freeze({
  client_types: CLIENT_TYPES,
  client_statuses: CLIENT_STATUSES,
  service_units: SERVICE_UNITS,
  service_categories: SERVICE_CATEGORIES,
  quote_statuses: QUOTE_STATUSES,
  discount_modes: DISCOUNT_MODES,
  stamp_duty_modes: STAMP_DUTY_MODES,
  vat_regimes: Object.values(VAT_REGIMES),
  defaults: Object.freeze({
    currency: CURRENCY,
    locale: LOCALE,
    numbering_template: NUMBERING_TEMPLATE,
    inps: INPS_DEFAULTS,
    stamp_duty: STAMP_DUTY,
    legal_note: FORFETTARIO_LEGAL_NOTE,
    validity_days: 30,
  }),
});
